/**
 * Dossiê: a correlação da entidade, montada por SQL a partir de contato compartilhado
 * (o mesmo telefone/e-mail em duas pessoas/empresas), sócios, condomínios, veículos, processos.
 * É onde o "enriquecer o grafo" fica visível: cadastrar um telefone que já era de
 * outra pessoa não duplica nada — faz as duas aparecerem aqui como vizinhas.
 *
 * Ancorado na entidade pelo par (ownerTipo, id) — vale para pessoa, empresa, condomínio,
 * imóvel, veículo e processo. Pessoa traz a visão rica; as demais, seus vínculos em `partes`.
 */
import { Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/misc';
import { api, type DossieCad } from '@/lib/api';
import { rotuloRelacao } from '@/lib/relacoesPessoais';
import { rotaEntidade } from '@/lib/recursos';
import type { OwnerTipo } from '@/lib/recursos/tipos';

/**
 * O nome de uma entidade correlacionada, clicável quando se sabe QUEM ela é.
 *
 * O dossiê nasceu como texto puro: ele dizia "esta pessoa divide o telefone com a ACME LTDA" e
 * deixava o operador procurar a ACME na lista, à mão. A informação estava certa e o caminho estava
 * cortado — numa ferramenta de investigação, puxar o fio é o ato principal.
 *
 * Sem `id` a linha continua sendo texto, e isso é de propósito: link que não leva a lugar nenhum é
 * pior que ausência de link.
 */
function Alvo({ tipo, id, children }: { tipo?: string; id?: string | null; children: React.ReactNode }) {
  if (!tipo || !id) return <>{children}</>;
  return (
    <Link to={rotaEntidade(tipo, id)} className="text-accent hover:underline">
      {children}
    </Link>
  );
}

/** O selo de tipo ao lado do nome, quando a entidade não é pessoa. */
function Tipo({ tipo }: { tipo: string }) {
  if (tipo === 'pessoa') return null;
  return (
    <span className="ml-1 rounded bg-surface-2 px-1.5 py-0.5 text-[11px] uppercase text-text-tertiary">
      {tipo}
    </span>
  );
}

export function PainelDossie({ ownerTipo, ownerId }: { ownerTipo: OwnerTipo; ownerId: string }) {
  const [dossie, setDossie] = useState<DossieCad | null>(null);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    setDossie(null);
    if (!ownerId) return;
    let vivo = true;
    setCarregando(true);
    void api.get<DossieCad>(`/cadastro/dossie/${ownerTipo}/${ownerId}`).then((r) => {
      if (!vivo) return;
      setCarregando(false);
      if (r.ok) setDossie(r.dados);
    });
    return () => {
      vivo = false;
    };
  }, [ownerTipo, ownerId]);

  return (
    <Card className="superficie-vidro relative z-[1]">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="size-4 text-accent" />
          Dossiê — correlações
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {/* Ternário, e não `estadoDaLista`: é ele que estreita o tipo de `dossie` para não-nulo
            no ramo de baixo. Com a função, o corpo inteiro precisaria de `dossie!` — trocar uma
            repetição por vinte asserções de tipo não é ganho. */}
        {carregando ? (
          <Spinner />
        ) : !dossie ? (
          <p className="tipo-corpo-pequeno text-text-tertiary">Sem dados de correlação.</p>
        ) : (
          <>
            {dossie.vizinhos.length > 0 && (
              <Secao titulo="Quem compartilha contato">
                {dossie.vizinhos.map((v, i) => (
                  <p key={i} className="tipo-corpo-pequeno text-text">
                    <Alvo tipo={v.tipo} id={v.entidade_id}>
                      {v.nome}
                    </Alvo>
                    <Tipo tipo={v.tipo} />{' '}
                    <span className="text-text-tertiary">
                      (via {v.via}: {v.valor})
                    </span>
                  </p>
                ))}
              </Secao>
            )}
            {dossie.partes.length > 0 && (
              <Secao titulo={rotuloPartes(dossie.entidade_tipo)}>
                {dossie.partes.map((p, i) => (
                  <p key={i} className="tipo-corpo-pequeno text-text">
                    <Alvo tipo={p.tipo} id={p.id}>
                      {p.rotulo}
                    </Alvo>
                    <Tipo tipo={p.tipo} />{' '}
                    {p.papel && <span className="text-text-tertiary">({p.papel})</span>}
                  </p>
                ))}
              </Secao>
            )}
            {dossie.relacoes.length > 0 && (
              <Secao titulo="Relações pessoais">
                {dossie.relacoes.map((r, i) => (
                  <p key={i} className="tipo-corpo-pequeno text-text">
                    <Alvo tipo="pessoa" id={r.pessoa_id}>
                      {r.nome}
                    </Alvo>{' '}
                    <span className="text-text-tertiary">({rotuloRelacao(r.tipo)})</span>
                  </p>
                ))}
              </Secao>
            )}
            {/* "Empresas" dizia o ONDE e escondia o QUÊ: um residencial geriátrico aparecia
                igualzinho a uma sociedade. O vínculo é a informação da linha. */}
            {dossie.empresas.length > 0 && (
              <Secao titulo="Empresas e instituições">
                {dossie.empresas.map((e, i) => (
                  <p key={i} className="tipo-corpo-pequeno text-text">
                    <Alvo tipo="empresa" id={e.empresa_id}>
                      {e.razao_social}
                    </Alvo>{' '}
                    <span className="text-text-tertiary">
                      — {e.vinculo}
                      {e.ate ? ' (encerrado)' : ''}
                    </span>
                  </p>
                ))}
              </Secao>
            )}
            {dossie.condominios.length > 0 && (
              <Secao titulo="Condomínios">
                {dossie.condominios.map((c, i) => (
                  <p key={i} className="tipo-corpo-pequeno text-text">
                    <Alvo tipo="condominio" id={c.condominio_id}>
                      {c.nome}
                    </Alvo>{' '}
                    <span className="text-text-tertiary">({c.papel})</span>
                  </p>
                ))}
              </Secao>
            )}
            {dossie.veiculos.length > 0 && (
              <Secao titulo="Veículos">
                {dossie.veiculos.map((v, i) => (
                  <p key={i} className="tipo-corpo-pequeno text-text">
                    <Alvo tipo="veiculo" id={v.id}>
                      {[v.marca, v.modelo].filter(Boolean).join(' ') || '—'}
                    </Alvo>{' '}
                    {v.placa && <span className="tipo-dado text-text-tertiary">{v.placa}</span>}
                  </p>
                ))}
              </Secao>
            )}
            {dossie.processos.length > 0 && (
              <Secao titulo="Processos">
                {dossie.processos.map((p, i) => (
                  <p key={i} className="tipo-corpo-pequeno text-text">
                    <Alvo tipo="processo" id={p.id}>
                      <span className="tipo-dado">{p.numero_cnj}</span>
                    </Alvo>{' '}
                    <span className="text-text-tertiary">({p.polo})</span>
                  </p>
                ))}
              </Secao>
            )}
            {/* Recebíveis: a seção que faltava, e cuja ausência deixava um sacado de oito títulos
                com o dossiê em branco. A OUTRA ponta vem nomeada e linkável — um título que diz
                «você é sacado» sem dizer de quem é metade da informação. */}
            {dossie.titulos.length > 0 && (
              <Secao titulo="Recebíveis">
                {dossie.titulos.map((t) => (
                  <p key={t.id} className="tipo-corpo-pequeno text-text">
                    <span className="text-text-secondary">
                      {t.papel === 'cedente' ? 'Cedeu' : 'Deve'}
                    </span>{' '}
                    {t.valor_nominal != null && (
                      <span className="tipo-dado">
                        {t.valor_nominal.toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                          maximumFractionDigits: 0,
                        })}
                      </span>
                    )}{' '}
                    <span className="text-text-secondary">
                      {t.papel === 'cedente' ? 'contra' : 'a'}
                    </span>{' '}
                    <Alvo tipo={t.outra_parte.tipo} id={t.outra_parte.id}>
                      {t.outra_parte.rotulo}
                    </Alvo>
                    <span className="text-text-tertiary">
                      {t.seu_numero ? ` · nº ${t.seu_numero.trim()}` : ''}
                      {t.venc_original
                        ? ` · vence ${t.venc_original.split('-').reverse().join('/')}`
                        : ''}
                      {t.carteira.rotulo ? ` · ${t.carteira.rotulo}` : ''}
                    </span>
                  </p>
                ))}
                {dossie.titulos_total > dossie.titulos.length && (
                  <p className="tipo-corpo-pequeno text-text-tertiary">
                    Mostrando {dossie.titulos.length} de {dossie.titulos_total} — a lista completa
                    está na ficha da carteira.
                  </p>
                )}
              </Secao>
            )}
            {dossie.transacoes.length > 0 && (
              <Secao titulo="Transações">
                {dossie.transacoes.map((t) => (
                  <p key={t.id} className="tipo-corpo-pequeno text-text">
                    <Alvo tipo="transacao" id={t.id}>
                      {t.descricao || t.natureza.replace(/_/g, ' ')}
                    </Alvo>{' '}
                    <span className="text-text-tertiary">
                      ({t.papel}
                      {t.data && ` · ${t.data.split('-').reverse().join('/')}`}
                      {t.co_partes.length > 0 && (
                        <>
                          {' · com '}
                          {t.co_partes.map((c, j) => (
                            <span key={`${c.tipo}:${c.id}`}>
                              {j > 0 && ', '}
                              <Alvo tipo={c.tipo} id={c.id}>
                                {c.rotulo}
                              </Alvo>
                            </span>
                          ))}
                        </>
                      )}
                      )
                    </span>
                  </p>
                ))}
                {dossie.transacoes_total > dossie.transacoes.length && (
                  <p className="tipo-corpo-pequeno text-text-tertiary">
                    Mostrando {dossie.transacoes.length} de {dossie.transacoes_total} — a lista
                    completa está na ficha da entidade.
                  </p>
                )}
              </Secao>
            )}
            {dossie.vizinhos.length === 0 &&
              dossie.partes.length === 0 &&
              dossie.relacoes.length === 0 &&
              dossie.empresas.length === 0 &&
              dossie.condominios.length === 0 &&
              dossie.veiculos.length === 0 &&
              dossie.processos.length === 0 &&
              dossie.transacoes.length === 0 &&
              dossie.titulos.length === 0 && (
                <p className="tipo-corpo-pequeno text-text-tertiary">
                  Sem correlações ainda — conforme a base cresce, elas aparecem aqui.
                </p>
              )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** Título da seção de vínculos estruturais, conforme o tipo da entidade âncora. */
function rotuloPartes(entidadeTipo: string): string {
  switch (entidadeTipo) {
    case 'empresa':
      return 'Sócios e funcionários';
    case 'condominio':
      return 'Moradores e síndico';
    case 'imovel':
      return 'Proprietários e moradores';
    case 'veiculo':
      return 'Proprietários e condutores';
    case 'processo':
      return 'Partes do processo';
    case 'transacao':
      return 'Partes e bens';
    default:
      return 'Vínculos';
  }
}

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="tipo-rotulo text-text-tertiary">{titulo}</span>
      <div className="mt-1 flex flex-col gap-0.5">{children}</div>
    </div>
  );
}
